import React from "react";
import posthog from "posthog-js";
import { POSTHOG_IDENTITY_FLAGS } from "utils/posthogFeatureFlags";

export type ApplicationShellPreference = "legacy" | "redesigned";

export interface ApplicationShellPreferenceContextValue {
  canChoose: boolean;
  preference: ApplicationShellPreference;
  chooseMode: (mode: ApplicationShellPreference) => void;
}

export const isApplicationShellRedesigned = (
  shellPreference:
    | Pick<ApplicationShellPreferenceContextValue, "preference">
    | null
    | undefined,
) => shellPreference?.preference === "redesigned";

const ApplicationShellPreferenceContext =
  React.createContext<ApplicationShellPreferenceContextValue | null>(null);

export interface ApplicationShellPreferenceProviderProps {
  children: React.ReactNode;
  canChoose: boolean;
  redesignEnabled: boolean | undefined;
}

export const ApplicationShellPreferenceProvider: React.FC<
  ApplicationShellPreferenceProviderProps
> = ({ children, canChoose, redesignEnabled }) => {
  const preference = redesignEnabled ? "redesigned" : "legacy";

  const chooseMode = React.useCallback(
    (mode: ApplicationShellPreference) => {
      if (!canChoose) return;

      posthog.updateEarlyAccessFeatureEnrollment(
        POSTHOG_IDENTITY_FLAGS.APP_REDESIGN_EARLY_ACCESS,
        mode === "redesigned",
      );
    },
    [canChoose],
  );

  const value = React.useMemo<ApplicationShellPreferenceContextValue>(
    () => ({ canChoose, preference, chooseMode }),
    [canChoose, chooseMode, preference],
  );

  return (
    <ApplicationShellPreferenceContext.Provider value={value}>
      {children}
    </ApplicationShellPreferenceContext.Provider>
  );
};

ApplicationShellPreferenceProvider.displayName =
  "ApplicationShellPreferenceProvider";

export const useApplicationShellPreference =
  (): ApplicationShellPreferenceContextValue | null =>
    React.useContext(ApplicationShellPreferenceContext);
